/**
 * Pairs management page.
 * Tabbed list (List / Matching) with {@link CollapseReplaceWrapper} for tutor create/edit forms.
 */

import * as React from 'react';
import { useSearchParams, useLocation, useNavigationType } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { PairsDataTable } from '@/components/pairs/inperson/PairsDataTable';
import { usePageActions } from '@/hooks/usePageActions';
import { ManagementPage } from '@/components/common/ManagementPage';
import { CollapseReplaceWrapper } from '@/components/common/CollapseReplaceWrapper';
import { CreateTutorView } from '@/components/tutors/inperson/CreateTutorView';
import { EditTutorView } from '@/components/tutors/inperson/EditTutorView';
import { ConfigureMatchingView } from '@/components/pairs/inperson/matching/ConfigureMatchingView';
import {
  PAIRS_TAB_COLORS,
  PAIRS_TAB_ICONS,
  PAIRS_TAB_LABELS,
  type PairsPageTab,
} from '@/components/pairs/pair-configs';
import type { TabItem } from '@/components/ui/tabs';

/** Which form is mounted in the collapse wrapper. */
type ActiveForm = 'create' | 'edit' | null;

/** In-person pairs list; create/edit tutor forms replace the table. */
export const PairsManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigationType = useNavigationType();
  const { canCreate, canDeleteRow, canExportCsv, canViewRow } = usePageActions('pairs');
  const accessibleTabs = React.useMemo<PairsPageTab[]>(
    () => (canCreate ? ['list', 'matching'] : ['list']),
    [canCreate],
  );
  const tabItems = React.useMemo<TabItem<PairsPageTab>[]>(() => {
    const items: TabItem<PairsPageTab>[] = [];
    for (const id of accessibleTabs) {
      const Icon = PAIRS_TAB_ICONS[id];
      items.push({
        id,
        label: PAIRS_TAB_LABELS[id],
        icon: <Icon className="h-4 w-4" />,
        color: PAIRS_TAB_COLORS[id],
      });
    }
    return items;
  }, [accessibleTabs]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [expandedPairId, setExpandedPairId] = React.useState<string | null>(null);

  const [activeForm, setActiveForm] = React.useState<ActiveForm>(null);
  const [editTutorId, setEditTutorId] = React.useState<string | null>(null);

  const initialTab = (location.state as { initialTab?: PairsPageTab } | null)?.initialTab ?? 'list';
  const [activeTab, setActiveTab] = React.useState<PairsPageTab>(() => {
    if (navigationType === 'POP') {
      const saved = sessionStorage.getItem('dt-tab:pairs-inperson') as PairsPageTab | null;
      if (saved) return saved;
    }
    return initialTab;
  });

  React.useEffect(() => {
    sessionStorage.setItem('dt-tab:pairs-inperson', activeTab);
  }, [activeTab]);

  React.useEffect(() => {
    if (!accessibleTabs.includes(activeTab)) {
      setActiveTab(accessibleTabs[0]);
    }
  }, [accessibleTabs, activeTab]);

  /** Open the tutor create form (pairs are built from sessions, not created here). */
  const handleRequestCreateTutor = () => {
    setActiveForm('create');
    setEditTutorId(null);
  };

  /** Close the form and drop the edit id so the next open starts clean. */
  const closeForm = React.useCallback(() => {
    setActiveForm(null);
    setEditTutorId(null);
  }, []);

  /** Close the form and refresh tutor / onboarding lists. */
  const handleFormSuccess = React.useCallback(() => {
    setActiveForm(null);
    setEditTutorId(null);
    void queryClient.invalidateQueries({ queryKey: ['users-search', 'tutor'] });
    void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
  }, [queryClient]);

  /** Deep-link: expand a pair row via `?view=`. */
  React.useEffect(() => {
    const viewId = searchParams.get('view');
    if (viewId) {
      setExpandedPairId(viewId);
    } else {
      setExpandedPairId(null);
    }
  }, [searchParams]);

  /** Keep `?view=` in sync so refresh / share reopen the same row. */
  const handleTogglePair = React.useCallback(
    (id: string) => {
      if (expandedPairId === id) {
        setExpandedPairId(null);
        searchParams.delete('view');
        setSearchParams(searchParams, { replace: true });
      } else {
        setExpandedPairId(id);
        searchParams.set('view', id);
        setSearchParams(searchParams, { replace: true });
      }
    },
    [expandedPairId, searchParams, setSearchParams],
  );

  /** Clear the expanded row and strip `?view=` from the URL. */
  const handleClosePairDetails = React.useCallback(() => {
    setExpandedPairId(null);
    searchParams.delete('view');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  function handleTabChange(tabId: string): void {
    setActiveTab(tabId as PairsPageTab);
    closeForm();
    handleClosePairDetails();
  }

  /** Stable JSX node for the form — avoids unmounting {@link EditTutorView} on re-renders. */
  const formContent = React.useMemo<React.ReactNode>(() => {
    if (activeForm === 'create') {
      return <CreateTutorView onSuccess={handleFormSuccess} onClose={closeForm} />;
    }
    if (activeForm === 'edit' && editTutorId) {
      return (
        <EditTutorView
          key={editTutorId}
          tutorUserId={editTutorId}
          onSuccess={handleFormSuccess}
          onClose={closeForm}
        />
      );
    }
    return null;
  }, [activeForm, editTutorId, handleFormSuccess, closeForm]);

  function renderListTab(): React.ReactNode {
    if (activeTab !== 'list') return null;
    return (
      <CollapseReplaceWrapper
        mode="replace"
        isOpen={activeForm !== null}
        collapsePosition="top"
        formContent={formContent}
      >
        <PairsDataTable
          expandedRowId={expandedPairId}
          onToggleRow={handleTogglePair}
          onCloseDetails={handleClosePairDetails}
          pageActions={{ canViewRow, canDeleteRow, canExportCsv }}
        />
      </CollapseReplaceWrapper>
    );
  }

  function renderMatchingTab(): React.ReactNode {
    if (activeTab !== 'matching') return null;
    return <ConfigureMatchingView onRequestCreateTutor={handleRequestCreateTutor} />;
  }

  return (
    <ManagementPage
      title="Gestion des Binômes"
      subtitle="Gérez les binômes élève–tuteur par séance"
      tabs={{
        items: tabItems,
        activeTab,
        onTabChange: handleTabChange,
      }}
    >
      {renderListTab()}
      {renderMatchingTab()}
    </ManagementPage>
  );
};
